import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import styled from 'styled-components'
import {
  useGet24HEthVolumeEthByAddressQuery,
  useLazyGetMemecoinByAddressQuery,
} from '../../../modules/memecoins/api'
import { selectMemecoinByAddress, selectShouldRefetchCurrentMemecoin } from '../../../modules/memecoins/selectors'
import { appendMemecoin, setShouldRefetchCurrentMemecoin } from '../../../modules/memecoins/slice'
import { Box } from '../../../shared_components/UI/Box'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { Memecoin, TransactionEvent } from '../../../types/reducerTypes'
import Invest from './Invest'
import { setActiveTokenSaleAddress } from '../../../modules/web3/actions'
import AllTradesTable from './TradesTable/AllTradesTable'
import { PageLink as PageTogglerLink } from '../../DropDetails/styles'
import MemecoinThreads from './Threads'
import {
  H2,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import { HelmetProvider } from '../../../App'
import Globe01 from '../../../shared_components/Icons/Globe01'
import CustomTradingView from './TradingView/CustomTradingView'

import {
  pagePaddingCssRules,
} from '../../../shared_components/Header/styles'

import ScrollToTop from '../../../shared_components/ScrollToTop'
import useTokenContractValues from '../useTokenContractValues'
import NotFound from '../../DropDetails/NotFound'
import {
  channels,
  useWebSocketContext,
} from '../../../contexts/WebSocketContext'
import {
  selectAccount,
  selectEthUsdPrice,
} from '../../../modules/web3/selectors'
import {
  getLatestMemecoinPriceInEth,
  getMemecoinMarketCap,
} from '../../../utils'
import { copyToClipboard } from '../../Dashboard/Vault'
import { toast } from 'react-toastify'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import routes from '../../../router/routes'
import { amountFromWei } from '../../../utils/amount'
import { barrySurface, useShouldShowForThreshold } from '../../../theme'
import { BackButton, PureBackButton } from '../../../shared_components/Page'
import { useCarbsOrKeto } from '../../Home/Season/CarbsOrKeto'
import { selectCurrentSeasonProgress } from '../../../modules/seasons/selectors'
import { formatNumberToFirstDecimalDigit } from './helpers'
import { getTwentyFourHourPctChange } from '../TokensList'
import { TwentyFourHourPriceChange } from '../../../shared_components/Library/Cards/LaunchCard'
import { useWindowSize } from '@uidotdev/usehooks'

export const KIRA_ADDRESS = '0xd1841F04f5653e524AC35b8D455d7891fc6aB9e4'
//export const KIRA_ADDRESS = '0xF83198464194f36e61dF4060E4F8e082A7eeD921'

export const KIRA_DETAILS = {
  liquidityPoolAddress: '0xea67923a7d0a3837f57d1f5e9d1cee2b158bb525',
  meta: {
    ethRaised: {
      updatedAt: '2024-10-03T06:39:44.624Z',
      value: '5000000000000000000',
    },
    ethOutForOneToken: {
      value: '10468453319',
      updatedAt: '2024-10-03T06:39:44.624Z',
    },
    totalTxns: 2942,
    marketCap: 23163.274550597984,
    latestTxnTimestamp: '2024-10-03T06:39:44.629Z',
  },
}

export function hotfixKiraCoin(memecoin: Memecoin) {
  return {
    ...JSON.parse(JSON.stringify(memecoin ?? {})),
    ...KIRA_DETAILS,
  }
}

enum TokenDetailsFilter {
  Trade = 'Trade',
  Comments = 'Comments',
  Txns = 'Txns',
  Chart = 'Chart',
}

export const useMemecoinDetails = (address: string) => {
  const shouldRefetchCurrentMemecoin = useSelector(selectShouldRefetchCurrentMemecoin)
  const [fetchMemecoinByAddress, { isLoading }] =
    useLazyGetMemecoinByAddressQuery()
  const memecoin = useSelector(selectMemecoinByAddress(address))
  const dispatch = useDispatch()

  const hasMemecoin = !!memecoin
  const isKira = address === KIRA_ADDRESS
  const memecoinToLoad = isKira
    ? !!memecoin
      ? hotfixKiraCoin(memecoin)
      : undefined
    : memecoin

  const handleFetchMemecoinByAddress = useCallback(async () => {
    const { memecoin } = await fetchMemecoinByAddress({ address }).unwrap()
    dispatch(appendMemecoin(memecoin))
    dispatch(setShouldRefetchCurrentMemecoin(false))
  }, [dispatch, fetchMemecoinByAddress, address])

  useEffect(() => {
    if (hasMemecoin) return
    handleFetchMemecoinByAddress()
  }, [handleFetchMemecoinByAddress, hasMemecoin])

  useEffect(() => {
    if (!shouldRefetchCurrentMemecoin) return
    handleFetchMemecoinByAddress()
  }, [shouldRefetchCurrentMemecoin])

  return useMemo(
    () => ({
      memecoin: memecoinToLoad,
      isLoading,
    }),
    [memecoinToLoad, isLoading],
  )
}

const TokenPage = (props: { match: { params: { address: string } } }) => {
  const { address } = props.match.params
  const dispatch = useDispatch()
  const { memecoin, isLoading } = useMemecoinDetails(address)
  const [isFirstRender, setIsFirstRender] = useState(true)

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false)
      return
    }
  }, [address])

  useEffect(() => {
    if (!memecoin?.tokenAddress) return
    dispatch(setActiveTokenSaleAddress(memecoin.tokenAddress))
  }, [memecoin, dispatch])

  return (
    <Wrapper dc>
      {isFirstRender && <ScrollToTop />}
      {!memecoin && isLoading ? (
        <Box c>
          <AnimatedLoader />
        </Box>
      ) : !memecoin && !isLoading ? (
        <NotFound />
      ) : (
        <>
          <HelmetProvider title={`ZAP | ${memecoin.tokenName}`} />
          <PageBody memecoin={memecoin} />
        </>
      )}
    </Wrapper>
  )
}

const defaultIcon = '/memecoin-launcher/socials/globe-01.svg'
const socialsIconsMap = {
  twitter: '/memecoin-launcher/socials/twitter.svg',
  x: '/memecoin-launcher/socials/twitter.svg',
  telegram: '/memecoin-launcher/socials/telegram.svg',
  discord: '/memecoin-launcher/socials/discord.svg',
  website: defaultIcon,
}

function PageBody({ memecoin }: { memecoin: Memecoin }) {
  const [marketCap, setMarketCap] = useState(memecoin?.meta?.marketCap)
  const { subscribe, unsubscribe } = useWebSocketContext()
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('compact')
  const isAtLeastExpandedDisplaySize = useShouldShowForThreshold('medium')
  const currentSeasonProgress = useSelector(selectCurrentSeasonProgress)

  const { pathname } = useLocation()

  const tokenSymbol = memecoin?.tokenSymbol

  const carbsOrKeto = useCarbsOrKeto()

  const logoUrl = memecoin?.media?.logo?.url || '/meme-icon.png'
  const bannerUrl = memecoin?.media?.banner?.url
  const hasBanner = !!bannerUrl

  const ethPriceInUsd = useSelector(selectEthUsdPrice)

  const account = useSelector(selectAccount)

  let { path, url } = useRouteMatch()
  const { push } = useHistory()
  const dispatch = useDispatch()

  const pageUrl = window.location.href

  const desktopFilters = [
    {
      label: TokenDetailsFilter.Comments,
      icon: '/img/icons/buttons/comments.svg',
      to: `${url}/comments`,
    },
    {
      label: TokenDetailsFilter.Txns,
      icon: '/img/icons/buttons/transactions.svg',
      to: `${url}/trades`,
    },
    {
      label: TokenDetailsFilter.Chart,
      icon: '/img/icons/buttons/chart.svg',
      to: `${url}/chart`,
    },
  ]

  const filters = [
    {
      label: TokenDetailsFilter.Trade,
      icon: '/img/icons/buttons/trade.svg',
      to: `${url}`,
    },
    ...desktopFilters,
  ]

  // Reat time market cap update for graduated pools
  useEffect(() => {
    if (!memecoin.liquidityPoolAddress) return

    const channel = channels.poolUpdated(memecoin.poolAddress)

    subscribe('token-page-market-cap', channel, (message) => {
      setMarketCap(message.data.meta.marketCap)
      dispatch(setShouldRefetchCurrentMemecoin(true))
    })

    return () => {
      unsubscribe('token-page-market-cap', channel)
    }
  }, [
    memecoin.poolAddress,
    memecoin.liquidityPoolAddress,
    subscribe,
    unsubscribe,
  ])

  // update quest progress for user
  useEffect(() => {
    if (!account || !currentSeasonProgress) return

    const channel = channels.questProgressByAddress(account)

    subscribe('quest-progress-for-user', channel, (message) => {
      carbsOrKeto(message.data, tokenSymbol)
      dispatch(setShouldRefetchCurrentMemecoin(true))
    })

    return () => {
      unsubscribe('quest-progress-for-user', channel)
    }
  }, [account, subscribe, unsubscribe, currentSeasonProgress, tokenSymbol])

  // Reat time market cap update for live pools
  useEffect(() => {
    if (memecoin.liquidityPoolAddress) return

    const getMarketCap = (latestTransaction: TransactionEvent) => {
      const tokenPriceInEth = getLatestMemecoinPriceInEth(
        latestTransaction,
        memecoin.decimals ?? 6,
      )

      if (ethPriceInUsd && tokenPriceInEth) {
        const computedMCap = getMemecoinMarketCap(
          tokenPriceInEth,
          ethPriceInUsd,
        )
        setMarketCap(computedMCap)
      }
    }

    const channel = channels.poolTransactionsByAddress(memecoin.poolAddress)

    subscribe('token-page-latest-tx-for-mCap', channel, (message) => {
      dispatch(setShouldRefetchCurrentMemecoin(true))
      getMarketCap(message.data)
    })

    return () => {
      unsubscribe('token-page-latest-tx-for-mCap', channel)
    }
  }, [
    memecoin.poolAddress,
    memecoin.liquidityPoolAddress,
    subscribe,
    unsubscribe,
    ethPriceInUsd,
  ])

  const onClickBack = () => push(routes.home)

  const activeFilter =
    (isAtLeastMediumDisplaySize ? desktopFilters : filters).find(
      ({ to }) => to === pathname,
    )?.label ?? TokenDetailsFilter.Chart

  const copy = useCallback(async (url: string) => {
    try {
      await copyToClipboard(url)
      toast.success('Copied to clipboard!')
    } catch (e) {
      console.error('Error copying to clipboard', e)
    }
  }, [])

  if (!isAtLeastMediumDisplaySize) {
    return (
      <Box dc>
        <Tabs ac g={48} style={{ overflow: 'scroll' }}>
          {filters.map(({ label, icon, to }) => (
            <TabItem ac jc dc g={6} key={to} isActive={activeFilter === label}>
              <PageLink to={to} query={''}>
                <Box ac dc jc g={6}>
                  <img src={icon} alt={label} />
                  <P s="S" fw={activeFilter === label ? 'semibold' : 'regular'}>
                    {label}
                  </P>
                </Box>
              </PageLink>
            </TabItem>
          ))}
        </Tabs>

        <Switch>
          <Route exact path={`${path}`}>
            <TradeToken memecoin={memecoin} marketCap={marketCap} />
          </Route>
          <Route path={`${path}/trades`}>
            <Container>
              <AllTradesTable memecoin={memecoin} />
            </Container>
          </Route>
          <Route path={`${path}/comments`}>
            <Container>
              <MemecoinThreads memecoin={memecoin} />
            </Container>
          </Route>
          <Route path={`${path}/chart`}>
            <Container>
              <ChartView memecoin={memecoin} />
            </Container>
          </Route>
        </Switch>
      </Box>
    )
  }

  return (
    <DeskTopContainer dc>
      {isAtLeastExpandedDisplaySize && (
        <Box
          jsb
          w="100%"
          style={{
            // paddingTop: 'var(--Spacing-Padding-L, 20px)',
            paddingBottom: 'var(--units-32, 32px)',
          }}
        >
          <Box ac>
            <PureBackButton onClick={onClickBack} padT={0} />
          </Box>
          <Box jfe ac g={8}>
            <BannerButton
              onClick={() => copy(pageUrl)}
              bst="alpha"
              bss="enabled"
            >
              <img src="/img/icons/buttons/share.svg" alt="share" />
            </BannerButton>
            <BannerButton
              onClick={() => copy(memecoin?.tokenAddress)}
              bst="alpha"
              bss="enabled"
            >
              <img src="/img/icons/buttons/copy.svg" alt="share" />
            </BannerButton>
          </Box>
        </Box>
      )}
      <Box g={isAtLeastExpandedDisplaySize ? 32 : 24} dc>
        <Box g={'var(--Index-Grids-Gutters, 24px)'}>
          <TradeToken memecoin={memecoin} marketCap={marketCap} />
        </Box>
        <LeftAlignedTabs ac g={20}>
          {desktopFilters.reverse().map(({ label, icon, to }) => (
            <TabItem
              ac
              jc
              dc
              g={6}
              key={to}
              // isActive={activeFilter === label}
              isActive={activeFilter === label}
            >
              <PageLink to={to} query={''}>
                <Box ac dc jc g={6}>
                  <img src={icon} alt={label} />
                  <P
                    s="S"
                    c={
                      activeFilter === label ? 'text-primary' : 'text-secondary'
                    }
                    fw={activeFilter === label ? 'semibold' : 'regular'}
                  >
                    {label === TokenDetailsFilter.Chart ? 'Info' : label}
                  </P>
                </Box>
              </PageLink>
            </TabItem>
          ))}
        </LeftAlignedTabs>
      </Box>
      <Box
        style={{
          padding: 'var(--Index-Grids-Gutters, 24px) 0',
        }}
      >
        <Switch>
          <Route exact path={`${path}`}>
            <Box dc g={'var(--Spacing-Padding-L, 20px)'}>
              <ChartView memecoin={memecoin} />
              <CoinsHeldCard memecoin={memecoin} />
              <MemecoinInfo memecoin={memecoin} marketCap={marketCap} />
            </Box>
          </Route>
          <Route path={`${path}/trades`}>
            <AllTradesTable memecoin={memecoin} />
          </Route>
          <Route path={`${path}/comments`}>
            <MemecoinThreads memecoin={memecoin} />
          </Route>
          <Route path={`${path}/chart`}>
            <Box dc g={'var(--Spacing-Padding-L, 20px)'}>
              <ChartView memecoin={memecoin} />
              <CoinsHeldCard memecoin={memecoin} />
            </Box>
          </Route>
        </Switch>
      </Box>
    </DeskTopContainer>
  )
}

const ChartView = ({ memecoin }) => {
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  return (
    <TradingViewBox mw="100%" id="trading-view-box">
      {!!memecoin &&
        (memecoin?.liquidityPoolAddress ? (
          <iframe
            id="dextools-widget"
            title="DEXTools Trading Chart"
            width="100%"
            height={isAtLeastMediumDisplaySize ? '390' : '706'}
            src={`https://www.dextools.io/widget-chart/en/base/pe-light/${memecoin.liquidityPoolAddress}`}
          />
        ) : (
          <CustomTradingView memecoin={memecoin} />
        ))}
    </TradingViewBox>
  )
}

const TradeToken = ({ memecoin, marketCap }) => {
  const ethPriceInUsd = useSelector(selectEthUsdPrice)
  const isAtlestMediumDisplaySize = useShouldShowForThreshold('compact')

  const { width: windowWidth } = useWindowSize()

  const logoUrl = memecoin?.media?.logo?.url || '/meme-icon.png'

  const tokenPriceInEth = useMemo(
    () =>
      getLatestMemecoinPriceInEth(
        memecoin.latestTransaction,
        memecoin.decimals ?? 6,
      ),
    [memecoin.latestTransaction, memecoin.decimals],
  )

  const tokenPrice = (tokenPriceInEth ?? 0) * (ethPriceInUsd ?? 0)

  const twentyFourHourPctChange = getTwentyFourHourPctChange(memecoin)

  return (
    <>
      {isAtlestMediumDisplaySize && <TokenImageAndProgress memecoin={memecoin} />}
      <Container>
        <TokenDetailsContainer ac g={8}>
          <TokenLogo src={logoUrl} alt="coin logo" />
          <Box g={8} dc>
            <H2 s="2XL" fw="bolder" uc ff="Proxima Nova">
              {memecoin?.tokenName}
            </H2>
            <Box g={4} ac>
              <P s="L" c="text-secondary">
                $
                {formatNumberToFirstDecimalDigit(
                  tokenPrice.toLocaleString('en-GB', {
                    minimumFractionDigits: 8,
                    maximumFractionDigits: 8,
                  }),
                )}
              </P>
              <TwentyFourHourPriceChange
                twentyFourHourPctChange={twentyFourHourPctChange}
              />
            </Box>
          </Box>
        </TokenDetailsContainer>
        <InvestBox>
          <Invest memecoin={memecoin} tokenPriceInEth={tokenPriceInEth} />
        </InvestBox>
        {!isAtlestMediumDisplaySize && (
          <>
            <InfoContainer pad={8}>
              <TokenStats memecoin={memecoin} marketCap={marketCap} />
            </InfoContainer>
            <TokenImageAndProgress memecoin={memecoin} />
            <MemecoinInfo memecoin={memecoin} marketCap={marketCap} />
            <CoinsHeldCard memecoin={memecoin} />
          </>
        )}
      </Container>
    </>
  )
}

const TokenImageAndProgress = ({ memecoin }) => {
  const { ethRaisedDecimal, totalEthToRaiseDecimal, percentageProgress } =
    useTokenContractValues({ memecoin })
  const isAtleastExpandedDisplay = useShouldShowForThreshold('medium')
  const url = window.location.href

  const { width: windowWidth } = useWindowSize()

  const logoUrl = memecoin?.media?.logo?.url || '/meme-icon.png'

  const copy = useCallback(async (url: string) => {
    try {
      await copyToClipboard(url)
      toast.success('Copied to clipboard!')
    } catch (e) {
      console.error('Error copying to clipboard', e)
    }
  }, [])

  const hidePercentageProgress = windowWidth < 320
    || windowWidth > 600 && windowWidth < 710
  
  return (
    <TokenLogoContainer>
      <Banner $bgUrl={logoUrl} />
      <ProgressBar $progress={percentageProgress} />
      <ProgressInfo>
        <Span s="L" c="text-secondary">
          {!hidePercentageProgress && (
            <Strong s="L" c="text-brandPrimary">
              {showDecimalsIfNeeded(percentageProgress)}%{' '}
            </Strong>
          )}
          <Strong s="L" c={hidePercentageProgress ? "text-brandPrimary" : "text-primary"}>
            {showDecimalsIfNeeded(ethRaisedDecimal)}{' '}
          </Strong>
          of {showDecimalsIfNeeded(totalEthToRaiseDecimal)} ETH
        </Span>
      </ProgressInfo>
      {!isAtleastExpandedDisplay && (
        <>
          <ShareButton onClick={() => copy(url)}>
            <img src="/img/icons/buttons/share.svg" alt="share" />
          </ShareButton>
          <CopyButton onClick={() => copy(memecoin.tokenAddress)}>
            <img src="/img/icons/buttons/copy.svg" alt="share" />
          </CopyButton>
        </>
      )}
    </TokenLogoContainer>
  )
}

const MemecoinInfo = ({ memecoin, marketCap }) => {
  const { socials } = memecoin

  const isAtlestMediumDisplaySize = useShouldShowForThreshold('compact')

  return (
    <InfoContainer dc>
      <Box g={20} dc>
        <Box dc alignSelf="flex-start" g={4}>
          <Strong uc s="L">
            {memecoin.tokenSymbol}
          </Strong>
          <P s="L" c="text-secondary">
            {memecoin.description}
          </P>
        </Box>
        {isAtlestMediumDisplaySize && (
          <>
            <Divider />
            <TokenStats memecoin={memecoin} marketCap={marketCap} />
          </>
        )}
        {!!socials.length && (
          <Box dc g={20}>
            <Divider />
            <SocialIcons g={16}>
              {socials.map(({ url, label }) => {
                const iconSrc = socialsIconsMap?.[label.toLowerCase()]
                return (
                  <a
                    href={url}
                    key={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={label}
                  >
                    {label.toLowerCase() !== 'website' ? (
                      <img src={iconSrc} alt={label} width={40} height={40} />
                    ) : (
                      <IconCont>
                        <Globe01 size={18} />
                      </IconCont>
                    )}
                  </a>
                )
              })}
            </SocialIcons>
          </Box>
        )}
      </Box>
    </InfoContainer>
  )
}

const TokenStats = ({ memecoin, marketCap }) => {
  const ethPriceInUsd = useSelector(selectEthUsdPrice)
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('compact')

  const { data } = useGet24HEthVolumeEthByAddressQuery({
    address: memecoin.poolAddress,
  })

  const ethVolume = data?.ethVolume ?? 0
  const ethVolumeInUsd = Number(amountFromWei(ethVolume, 18)) * ethPriceInUsd

  const textSize = isAtLeastMediumDisplaySize ? 'XL' : 'M'
  const secondaryTextSize = isAtLeastMediumDisplaySize ? 'M' : 'S'
  
  return (
    <Box jsb>
      <Box dc ac g={6}>
        <H2 s={textSize} c="text-primary" fw="bolder">
          {memecoin?.meta.totalTxns}
        </H2>
        <P s={secondaryTextSize} c="text-secondary">
          Txns
        </P>
      </Box>
      <Divider isVertical />
      <Box dc ac g={6}>
        <H2 s={textSize} c="text-primary" fw="bolder">
          $
          {marketCap?.toLocaleString('en-GB', {
            maximumFractionDigits: 2,
            notation: 'compact',
          })}
        </H2>
        <P s={secondaryTextSize} c="text-secondary">
          MarketCap
        </P>
      </Box>
      <Divider isVertical />
      <Box dc ac g={6}>
        <H2 s={textSize} c="text-primary" fw="bolder">
          $
          {ethVolumeInUsd?.toLocaleString('en-GB', {
            maximumFractionDigits: 2,
            notation: 'compact',
          })}
        </H2>
        <P s={secondaryTextSize} c="text-secondary">
          Vol
        </P>
      </Box>
    </Box>
  )
}

const CoinsHeldCard = ({ memecoin }) => {
  const { push } = useHistory()

  const { tokenSymbol } = memecoin
  const { decimals, userTokenBalance } = useTokenContractValues({ memecoin })

  const hanldeOnClickTokensHeld = () => {
    push(`${routes.myzap.tokensHeld}/${memecoin.poolAddress}`)
  }

  const logoUrl = memecoin?.media?.logo?.url || '/meme-icon.png'

  const tokenBalance = amountFromWei(userTokenBalance, decimals)

  if (!Number(tokenBalance)) return null

  return (
    <InfoContainer onClick={hanldeOnClickTokensHeld} bst="alpha" bss="enabled">
      <Box g={10}>
        <CoinsHeldLogoContainer>
          <img src="/img/icons/memecoins/coins-held.svg" alt="coins held" />
        </CoinsHeldLogoContainer>
        <Box dc>
          <P s="L" c="text-secondary">
            Your total
          </P>
          <Box>
            <TokenLogo src={logoUrl} alt="coin logo" size={20} />
            <BalanceContainer>
              <P s="L" c="text-primary">
                {showDecimalsIfNeeded(Number(tokenBalance))}
              </P>
              <P s="S" c="text-secondary">
                {tokenSymbol}
              </P>
            </BalanceContainer>
          </Box>
        </Box>
      </Box>
      <img src="/img/icons/buttons/arrow-right.svg" alt="back" />
    </InfoContainer>
  )
}

function PageLink({ to, query, children }) {
  const location = useLocation()
  const queryString = query ? `?${new URLSearchParams(query).toString()}` : ''
  const isActive = location.pathname === to

  return (
    <PageTogglerLink to={`${to}${queryString}`} $isActive={isActive}>
      <Strong s="L" fw={isActive ? 'semibold' : 'regular'}>
        {children}
      </Strong>
    </PageTogglerLink>
  )
}

const TradingViewBox = styled(Box)`
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(210, 212, 220, 0.25);
`

const InvestBox = styled(Box)``

export const InfluencersBox = styled(Box)``

const SocialIcons = styled(Box)`
  @media (max-width: 1000px) {
    justify-content: flex-start;
  }
`

export default TokenPage

const Wrapper = styled(Box)`
  padding-bottom: 3rem;
`

const Banner = styled.div<{ $bgUrl: string; $bgSize?: string }>`
  display: flex;
  aspect-ratio: 1;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  background: url(${(props) => props.$bgUrl}) lightgray 50% / cover no-repeat;
  border-radius: 12px;

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.minWidth}px) {
    max-width: 484px;
  }

  @media screen and (max-width: ${({ theme }) =>
    theme.layout.compact.indexGrids.maxWidth}px) {
    width: 100%;
  }
`

const IconCont = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  background: var(--surface-minimal-secondary, rgba(255, 255, 255, 0.08));
  border-radius: 4px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;

  @media screen and (max-width: ${({ theme }) =>
    theme.layout.compact.indexGrids.maxWidth}px) {
    ${pagePaddingCssRules};
    padding-top: var(--units-32, 32px) !important;
  }

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.minWidth}px) {
    padding: 0 !important;
  }
`

const TokenDetailsContainer = styled(Box)`
  align-items: center;
`

export const TokenLogo = styled.img<{ size?: number }>`
  width: 68px;
  height: 68px;

  ${({ size }) =>
    size &&
    `
    width: ${size}px;
    height: ${size}px;
  `};

  border-radius: var(--units-88, 88px);
  border: 1px solid var(--stroke-secondary, #333);
  background:
    url(<path-to-image>) lightgray 50% / cover no-repeat,
    var(--surface-primary, #080808);
`

const TokenLogoContainer = styled.div`
  position: relative;
  width: 100%;
  align-self: center;
`

const ProgressBar = styled.div<{ $progress: number }>`
  position: absolute;
  bottom: 0;
  width: ${({ $progress }) => `${$progress}%`};
  height: 4px;
  background: var(--surface-brand, #ff0);
  right: 4px;
  left: 4px;
  border-bottom-left-radius: 12px;
  ${({ $progress }) => $progress === 100 && 'border-bottom-right-radius: 12px;'}
`

const BannerButton = styled.div`
  display: flex;
  height: 40px;
  width: 40px;
  justify-content: center;
  align-items: center;
  border-radius: 97.778px;
  border: 1px solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);
  cursor: pointer;
  ${barrySurface}
`

const ShareButton = styled(BannerButton)`
  bottom: 16px;
  right: 66px;
  position: absolute;
  cursor: pointer;
`

const CopyButton = styled(BannerButton)`
  bottom: 16px;
  right: 16px;
  position: absolute;
  cursor: pointer;
`

const ProgressInfo = styled.div`
  display: flex;
  padding: 0 16px;
  height: 40px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  position: absolute;
  bottom: 16px;
  left: 16px;
  border-radius: 97.778px;
  border: 1.111px solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);
`

const CoinsHeldLogoContainer = styled.div`
  display: flex;
  width: 44.444px;
  height: 44.444px;
  justify-content: center;
  align-items: center;
  border-radius: var(--units-88, 88px);
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
`

const InfoContainer = styled(Box)<{ pad?: number }>`
  width: 100%;
  padding: ${({ pad }) => `var(--units-${pad ?? 24}px, ${pad ?? 24}px) var(--Spacing-Padding-M, ${pad ?? 16}px)`};
  align-items: center;
  gap: var(--units-20, 20px);
  border-radius: var(--units-12, 12px);
  border: 1px solid var(--stroke-tertiary, #252525);
  ${barrySurface}
`

const BalanceContainer = styled.td`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  white-space: nowrap;

  ${P} {
    margin-left: 4px; // Add a bit of space between the number and symbol if needed
  }
`

const Divider = styled.div<{ isVertical?: boolean }>`
  ${({ isVertical }) =>
    isVertical &&
    `
    width: 1px;
  `}

  align-self: stretch;
  border: 1px solid var(--stroke-tertiary, #252525);
`

const Tabs = styled(Box)`
  padding: var(--units-16, 16px) 16px 0px 16px;
  border-bottom: 1px solid var(--stroke-secondary, #333);
  background: linear-gradient(
      0deg,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%,
      var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%
    ),
    var(--surface-primary, #080808);
`

const LeftAlignedTabs = styled(Tabs)`
  justify-content: flex-start;
  background: none;
  padding-top: 16px;
`

const TabItem = styled(Box) <{ isActive: boolean }>`
  width: 60px;
  padding-bottom: 8px;
  position: relative;

  ${({ isActive }) =>
    isActive &&
    `
      cursor: default;
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 44px;
        border-radius: 30px;
        border-bottom: 2px solid var(--surface-brand, #FF0);
      }
  `}

  ${({ isActive }) =>
    !isActive &&
    `
    &:hover {
      cursor: pointer;
      color: var(--text-primary, #FFF);
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 44px;
        border-radius: 30px;
        border-bottom: 2px solid var(--surface-minimal-alpha-tertiary, rgba(255, 255, 255, 0.40));
      }
    }
  `}

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.minWidth}px) {
    max-width: 60px;
  }
`

const DeskTopContainer = styled(Box)`
  ${pagePaddingCssRules}
  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.minWidth}px) {
    padding-top: var(--Index-Grids-Margins, 24px);
  }

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.expanded.indexGrids.minWidth}px) {
    padding: var(--Index-Grids-Gutters, 32px) var(--Index-Grids-Margins, 48px);
  }
`
